'use client';

import React, { useCallback, useState } from 'react';
import { useTranslations } from 'use-intl';
import { TrashIcon } from '@heroicons/react/24/outline';
import useCloseFlyouts from '@/hooks/useCloseFlyouts';
import Image from '@/components/atoms/Image';
import Button from '@/components/atoms/button';
import Link from '@/components/atoms/link';
import Breadcrumb from '@/components/molecules/breadcrumb';
import InfoBanner from '@/components/molecules/info-banner';
import MinOrderSurchargeBanner from '@/components/molecules/min-order-surcharge-banner';
import SparePartsCarousel from '@/components/organisms/spare-parts-carousel';
import { pushCartCleared } from '@/lib/analytics/dataLayer';
import { Product } from '@/types/entity/product';
import CartOrderSummary from '../cart-order-summary';
import { CartFlushRegistryProvider } from './cart-flush-registry';
import CartContent from './components/cart-content';
import EmptyCartDialog from './components/empty-cart-dialog';
import { CartProps } from './types';
import type { ClearCartResult } from '@/lib/hooks/useCart/types';

const Cart = ({
  loading,
  transaction,
  lineItems: lineItemsProp,
  viewCartDisabled = false,
  quoteRequestDisabled = false,
  checkoutDisabled = false,
  invalidAddressesRequirements = false,
  creditAccountStopped = false,
  outOfStockBlocked = false,
  discountCodes,
  onApplyDiscount,
  discountReadOnly,
  onAdd,
  onClear,
  onSaveToNewList,
  canSaveToList = false,
  fisThreshold,
  spareParts = [],
  sparePartsCtBySku = {},
  ...props
}: CartProps) => {
  const translate = useTranslations();

  // EWM-760 review fix — lazy initializer reproduces the old mount-time
  // `useEffect` merge result exactly: on first mount the old effect ran with
  // `lineItems === lineItemsProp`, so every item matched itself and the
  // merge produced `lineItemsProp.map(item => ({ ...item, deleted: false }))`
  // (no `newItems`, since nothing was missing from the "existing" set). The
  // render-time adjust-state block below is skipped on mount by design
  // (`prevLineItemsProp` starts equal to `lineItemsProp`), so this initial
  // value must already carry that mount-time merge shape.
  const [lineItems, setLineItems] = useState<
    Array<Product & { deleted?: boolean; undoProcessing?: boolean }> | undefined
  >(() => lineItemsProp?.map((item) => ({ ...item, deleted: false })));

  const allItemsAreDeleted = !lineItems?.filter((item) => !item.deleted).length;

  // EWM-760 (was a setState-in-effect on `[lineItemsProp]`) — adjust-state-
  // during-render: merges server state into local, preserving locally-deleted
  // items so the undo-remove flow can restore them. `prevLineItemsProp` is
  // tracked unconditionally (including transitions through `undefined`) so
  // the render-time check fires on exactly the same reference changes the
  // old effect's dependency array would have; the `if (!lineItemsProp)
  // return` guard is preserved as-is around the merge itself.
  const [prevLineItemsProp, setPrevLineItemsProp] = useState(lineItemsProp);
  if (lineItemsProp !== prevLineItemsProp) {
    setPrevLineItemsProp(lineItemsProp);
    if (lineItemsProp) {
      setLineItems((lineItems) => {
        if (!lineItems) return lineItemsProp;

        const newItems = lineItemsProp.filter((lineItem) => !lineItems.find((item) => lineItem.id === item.id));

        return [
          ...lineItems
            .filter((item) => !item.undoProcessing)
            .map((lineItem) => {
              const item = lineItemsProp.find((item) => lineItem.id === item.id);

              return { ...(item ?? lineItem), deleted: !item };
            }),
          ...newItems,
        ];
      });
    }
  }

  const onUndoRemove = useCallback(
    async (lineItemId: string) => {
      const item = lineItems?.find((item) => item.id === lineItemId);

      const newItems = (lineItems ?? []).map((item) =>
        item.id !== lineItemId ? item : { ...item, undoProcessing: true },
      );

      setLineItems(newItems);

      if (item) await onAdd(item.sku ?? '', item.quantity ?? 1);
    },
    [lineItems, onAdd],
  );

  const onClearItem = (itemId: string) => {
    setLineItems((items) => items?.filter((item) => item.id !== itemId));
  };

  // EWM-791 — "Empty cart": the BFF retires the cart (delete, or freeze when it
  // carries payments; a fresh cart is minted on the next fetch) and the dialog
  // optionally saves the basket to a purchase list first. Local rows are
  // dropped immediately after a SUCCESSFUL clear so the empty state renders
  // without waiting for the SWR refetch.
  //
  // EWM-929 — that drop used to be unconditional. The BFF can refuse (the CT
  // 400 this ticket fixes, an EWM-552 checkout lock, a missing permission), and
  // blanking the rows anyway is exactly the reported defect: an empty-looking
  // page over a cart commercetools still holds, which fills back in on the next
  // navigation. The result is also returned so the dialog can stay open and say
  // why.
  const [emptyCartOpen, setEmptyCartOpen] = useState(false);
  const handleClearCart = useCallback(async (): Promise<ClearCartResult> => {
    // Unreachable in practice — the dialog is only rendered inside the
    // `onClear &&` branch below — but never report a success nobody granted.
    if (!onClear) return { success: false };

    // EWM-929 AC-7 — snapshot the DISPLAYED lines before the clear. `cart_cleared`
    // must report the same per-unit price as its per-row sibling `remove_from_cart`
    // (fired by cart-items-table), which is the EWM-523 live-price overlay the
    // customer is looking at — not commercetools' add-time price. Locally deleted
    // rows are excluded: they are already out of the cart server-side.
    const cleared = (lineItems ?? []).filter((item) => !item.deleted);

    const result = await onClear();
    if (!result.success) return result;

    setLineItems([]);
    pushCartCleared({
      currency: cleared[0]?.currency ?? '',
      items: cleared.map((item) => ({
        item_id: item.sku ?? item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity ?? 1,
        ...(item.brand ? { item_brand: item.brand } : {}),
      })),
    });
    return result;
  }, [onClear, lineItems]);

  const closeFlyouts = useCloseFlyouts();

  if (viewCartDisabled) {
    return (
      <div className="relative p-4 md:bg-neutral-200 md:px-5 md:py-6 lg:p-12">
        <div className="grid w-full place-items-center gap-12 rounded-lg bg-white py-9 pb-[80px]">
          <div className="grid place-items-center gap-6">
            <p className="text-16 md:text-18 lg:text-20 text-center text-gray-700">{translate('cart.view-disabled')}</p>

            <Image src="/images/locked-cart.png" className="w-[200px] md:w-[256px] lg:w-[328px]" alt="Cart locked" />
          </div>

          <p className="text-14 md:text-16 max-w-[720px] text-center leading-loose text-gray-600">
            {translate('cart.view-disabled-desc')}
          </p>
        </div>
      </div>
    );
  }

  const hasLineItems = (lineItems ?? []).filter((item) => !item.deleted).length > 0;
  const isViewOnly = !invalidAddressesRequirements && (quoteRequestDisabled || checkoutDisabled);

  return (
    <div>
      {isViewOnly && (
        <div className="border-b border-gray-100 px-4 py-3 md:px-8 lg:px-11">
          <InfoBanner>{translate('cart.view-only-banner')}</InfoBanner>
        </div>
      )}

      {invalidAddressesRequirements && (
        <div className="border-b border-gray-100 px-4 py-3 md:px-8 lg:px-11">
          <InfoBanner>
            <b>{translate('cart.checkout-unavailable')}</b>: {translate('cart.invalid-addresses-requirements')}
          </InfoBanner>
        </div>
      )}

      {creditAccountStopped && (
        <div className="border-b border-gray-100 px-4 py-3 md:px-8 lg:px-11">
          <InfoBanner>{translate('cart.credit-stopped-banner')}</InfoBanner>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-4 pt-8 pb-12">
        {/* EWM-564 — breadcrumbs, consistent with the rest of the site */}
        <nav aria-label={translate('common.breadcrumb')} className="mb-4">
          <Breadcrumb>
            <Link href="/" underlineOnHover={false}>
              {translate('common.home')}
            </Link>
            <span aria-current="page">{translate('cart.shopping')}</span>
          </Breadcrumb>
        </nav>
        <div className="mb-8 flex items-center justify-between gap-4">
          <h1 className="text-32 text-primary font-bold">{translate('cart.shopping')}</h1>
          {hasLineItems && !isViewOnly && onClear && (
            <Button
              variant="ghost"
              size="s"
              className="text-primary flex shrink-0 items-center gap-1"
              onClick={() => setEmptyCartOpen(true)}
            >
              <TrashIcon className="size-[16px]" aria-hidden />
              {translate('cart.empty-cart')}
            </Button>
          )}
        </div>

        {/* EWM-224 — warn early on the cart when below the minimum order value, where
            the customer can still add items. Self-gates at/above the threshold. */}
        {hasLineItems && transaction && <MinOrderSurchargeBanner subtotal={transaction.subtotal} className="mb-6" />}

        <main>
          {/* EWM-613 — the flush registry spans the qty rows (in CartContent) and the
              Proceed-to-Checkout button (in CartOrderSummary) so the latter can flush
              any pending debounced quantity before navigating to /checkout. */}
          <CartFlushRegistryProvider>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              <CartContent
                loading={loading}
                lineItems={lineItems ?? []}
                discountCodes={discountCodes}
                className={hasLineItems ? 'lg:col-span-2' : 'lg:col-span-3'}
                isViewOnly={isViewOnly}
                onAdd={onAdd}
                onUndoRemove={onUndoRemove}
                onClearItem={onClearItem}
                {...props}
              />

              {hasLineItems && (
                <aside className="lg:col-span-1">
                  <CartOrderSummary
                    className="min-w-[360px] lg:sticky lg:top-[180px]"
                    transaction={
                      transaction ?? {
                        subtotal: { centAmount: 0, currencyCode: 'AUD', fractionDigits: 2 },
                        discount: { centAmount: 0, currencyCode: 'AUD', fractionDigits: 2, segments: [] },
                        shipping: { centAmount: 0, currencyCode: 'AUD', fractionDigits: 2 },
                        tax: { centAmount: 0, currencyCode: 'AUD', fractionDigits: 2 },
                        total: { centAmount: 0, currencyCode: 'AUD', fractionDigits: 2 },
                      }
                    }
                    fisThreshold={fisThreshold}
                    checkoutDisabled={
                      checkoutDisabled || allItemsAreDeleted || creditAccountStopped || outOfStockBlocked
                    }
                    onCheckout={closeFlyouts}
                    discountCodes={discountCodes}
                    onApplyDiscount={onApplyDiscount}
                    discountReadOnly={discountReadOnly}
                  />
                </aside>
              )}
            </div>
          </CartFlushRegistryProvider>
        </main>
      </div>

      <SparePartsCarousel items={spareParts} ctBySku={sparePartsCtBySku} listId="widget_cart-spare-parts" />

      {onClear && (
        <EmptyCartDialog
          isOpen={emptyCartOpen}
          canSaveToList={canSaveToList}
          onClose={() => setEmptyCartOpen(false)}
          onClear={handleClearCart}
          onSaveToNewList={onSaveToNewList}
        />
      )}
    </div>
  );
};

export default Cart;
